import { FC, useMemo } from 'react';
import { getCssVariable } from '../../../../../utils';
import { PieChart } from '@mui/x-charts';
import { Box, Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import { East, NorthEast, SouthEast } from '@mui/icons-material';
import './DataTaskDistributionChart.scss';
import { useTranslation } from 'react-i18next';
export interface ChartDataItem {
  label: string;
  value: number;
  color: string;
}

interface TaskDistributionChartProps {
  data: ChartDataItem[];
  size?: number;
  className?: string;
  value?: string | number;
  diffNum?: number;
  color?: string;
}

const colorGrayLightest = getCssVariable('--color-gray-lightest') || '#e8eaed';
const colorGrayBase = getCssVariable('--color-gray-base') || '#b5b5bc';

const DataTaskDistributionChart: FC<TaskDistributionChartProps> = ({
  data,
  size = 156,
  className,
  value,
  diffNum,
  color,
}) => {
  const { t } = useTranslation();
  const hasDiffNum = diffNum !== undefined && diffNum !== null && diffNum !== 0;

  const pieChartData = useMemo(() => {
    if (!data.length) {
      return [
        {
          id: 0,
          value: 1,
          color: colorGrayLightest,
          label: t('data.common.noData'),
        },
      ];
    }

    return data.map((item, index) => ({
      id: index,
      value: item.value,
      color: item.color,
      label: item.label,
    }));
  }, [data, t]);

  const outerRadius = size / 2;
  const innerRadius = outerRadius - 12;

  return (
    <Box className={clsx('data-task-distribution-chart', className)}>
      <PieChart
        series={[
          {
            data: pieChartData,
            innerRadius: innerRadius,
            outerRadius: outerRadius,
            arcLabelMinAngle: 45,
            valueFormatter: (value) => value.toString(),
            cornerRadius: 3,
          },
        ]}
        width={size}
        height={size}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        slotProps={{
          legend: {
            hidden: true,
          },
        }}
        tooltip={{
          trigger: 'none',
        }}
        sx={{
          '& .MuiPieArcLabel-root': {
            fill: data.length > 0 ? 'white' : colorGrayBase,
            fontSize: 14,
            fontWeight: 'bold',
          },
        }}
        className="data-task-distribution-chart__pie"
      />
      <Stack ml={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          className="data-task-distribution-chart__title-container"
          gap={5}
        >
          <Typography variant="body1" className="data-task-distribution-chart__title">
            {t('data.task.doneTasks')}
          </Typography>
          <Stack direction={'row'} alignItems="center" gap={1}>
            <Typography variant="body1" className="data-task-distribution-chart__title">
              {value}
            </Typography>
            <Typography variant="body1" color={color}>
              {hasDiffNum ? (
                diffNum > 0 ? (
                  <>
                    <NorthEast className="data-task-distribution-chart__diff-icon" />{' '}
                    {Math.abs(Math.floor(diffNum))}
                  </>
                ) : (
                  <>
                    <SouthEast className="data-task-distribution-chart__diff-icon" />{' '}
                    {Math.abs(Math.floor(diffNum))}
                  </>
                )
              ) : (
                ''
              )}
            </Typography>
          </Stack>
        </Stack>
        {data.length > 0 && (
          <Box className="data-task-distribution-chart__legend">
            {data.map((item, index) => (
              <Stack
                direction={'row'}
                alignItems="center"
                justifyContent={'space-between'}
                key={index}
                className="data-task-distribution-chart__legend-item"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box
                    sx={{ backgroundColor: item.color }}
                    className="data-task-distribution-chart__legend-color"
                  />
                  <Typography variant="body2" className="data-task-distribution-chart__legend-text">
                    {item.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" className="data-task-distribution-chart__legend-text">
                  {item.value}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default DataTaskDistributionChart;
